import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, TextField, Stack, MenuItem, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactJson from 'react-json-view';

const exportFormats = [
  { label: 'Excel', value: 'xlsx' },
  { label: 'JSON', value: 'json' },
  { label: 'Text', value: 'txt' },
  { label: 'Word', value: 'docx' }
];

const ReviewFilePage = () => {
  const { projectId, converterId, fileId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [apiExporting, setApiExporting] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiDialogOpen, setApiDialogOpen] = useState(false);
  const [converter, setConverter] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [tableEditData, setTableEditData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/files/${fileId}`);
      setData(res.data.data);
      setEditData(res.data.data);
    } catch (err) {
      setError('Failed to load file data');
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/files/${fileId}/history`);
      setHistory(res.data.versionHistory);
    } catch {}
  };

  const fetchConverter = async () => {
    try {
      const res = await api.get(`/converters/${projectId}`);
      setConverter(res.data.find(c => c._id === converterId));
    } catch {}
  };

  const fetchFileMeta = async () => {
    try {
      const res = await api.get(`/files/${fileId}`);
      setFileMeta(res.data.meta || {});
    } catch {}
  };

  useEffect(() => {
    fetchData();
    fetchHistory();
    fetchConverter();
    fetchFileMeta();
    // eslint-disable-next-line
  }, [fileId]);

  useEffect(() => {
    // If exportFormat is 'xlsx', always show a table
    if (exportFormat === 'xlsx') {
      if (Array.isArray(data)) {
        setTableEditData(JSON.parse(JSON.stringify(data)));
      } else if (data && typeof data === 'object') {
        // If data is an object with a single array property, use that array
        const keys = Object.keys(data);
        if (keys.length === 1 && Array.isArray(data[keys[0]])) {
          setTableEditData(JSON.parse(JSON.stringify(data[keys[0]])));
        } else {
          setTableEditData([JSON.parse(JSON.stringify(data))]);
        }
      } else {
        setTableEditData(null);
      }
    } else if (Array.isArray(data)) {
      setTableEditData(JSON.parse(JSON.stringify(data)));
    } else {
      setTableEditData(null);
    }
  }, [data, exportFormat]);

  const handleChange = (key, value) => {
    setEditData(prev => ({ ...prev, [key]: value }));
  };

  const handleTableCellChange = (rowIdx, key, value) => {
    setTableEditData(prev => prev.map((row, i) => i === rowIdx ? { ...row, [key]: value } : row));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (tableEditData) {
        await api.put(`/files/${fileId}`, { result: tableEditData });
      } else {
        await api.put(`/files/${fileId}`, { result: editData });
      }
      fetchData();
      fetchHistory();
    } catch (err) {
      setError('Failed to save changes');
    }
    setSaving(false);
  };

  const handleExport = () => {
    window.open(`${api.defaults.baseURL}/files/${fileId}/export?format=${exportFormat}`, '_blank');
  };

  const handleApiExport = async () => {
    setApiExporting(true);
    setApiResponse(null);
    try {
      const res = await api.post(`/files/${fileId}/export-api`);
      setApiResponse(res.data);
      setApiDialogOpen(true);
    } catch (err) {
      setApiResponse({ error: err.response?.data?.message || 'API export failed', details: err.response?.data });
      setApiDialogOpen(true);
    }
    setApiExporting(false);
  };

  const renderPreview = () => {
    if (exportFormat === 'json' && data && typeof data === 'object') {
      return <ReactJson src={data} collapsed={2} name={false} displayDataTypes={false} />;
    }
    if (exportFormat === 'xlsx' && Array.isArray(tableEditData)) {
      const keys = tableEditData.length > 0 ? Object.keys(tableEditData[0]) : [];
      return (
        <Box sx={{ overflowX: 'auto', mt: 2 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {keys.map(key => <th key={key} style={{ border: '1px solid #ccc', padding: 4 }}>{key}</th>)}
              </tr>
            </thead>
            <tbody>
              {tableEditData.map((row, i) => (
                <tr key={i}>
                  {keys.map(key => (
                    <td key={key} style={{ border: '1px solid #ccc', padding: 4 }}>
                      <input
                        value={row[key] || ''}
                        onChange={e => handleTableCellChange(i, key, e.target.value)}
                        style={{ width: '100%', border: 'none', background: 'transparent' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      );
    }
    if (fileMeta && fileMeta.mimetype && fileMeta.mimetype.startsWith('image/')) {
      return <img src={`${api.defaults.baseURL}/files/${fileId}/download`} alt="preview" style={{ maxWidth: '100%', marginTop: 16 }} />;
    }
    if (exportFormat === 'txt' || exportFormat === 'docx') {
      return <pre style={{ background: '#f5f5f5', padding: 12, marginTop: 16 }}>{typeof data === 'string' ? data : JSON.stringify(data, null, 2)}</pre>;
    }
    return null;
  };

  return (
    <Box maxWidth={700} mx="auto" mt={4}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/project/${projectId}/converter/${converterId}`)}>
        Back to Converter
      </Button>
      <Typography variant="h5" mt={2}>Review & Edit Data</Typography>
      {loading ? <CircularProgress /> : data && (
        <Box mt={2}>
          {Array.isArray(tableEditData) ? null : (
            <>
              <Stack spacing={2}>
                {Object.keys(editData).map(key => (
                  <TextField
                    key={key}
                    label={key}
                    value={editData[key] || ''}
                    onChange={e => handleChange(key, e.target.value)}
                    fullWidth
                  />
                ))}
              </Stack>
              <Button variant="contained" sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Confirm'}
              </Button>
            </>
          )}
          <Box mt={4}>
            <Typography variant="h6">Preview</Typography>
            {renderPreview()}
          </Box>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Box mt={4}>
        <Typography variant="h6">Export</Typography>
        <TextField
          select
          label="Format"
          value={exportFormat}
          onChange={e => setExportFormat(e.target.value)}
          sx={{ width: 200, mr: 2 }}
        >
          {exportFormats.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={handleExport}>Download</Button>
        {converter && converter.exportSettings && converter.exportSettings.format === 'api' && (
          <Button variant="contained" color="secondary" sx={{ ml: 2 }} onClick={handleApiExport} disabled={apiExporting}>
            {apiExporting ? 'Exporting...' : 'Send to API'}
          </Button>
        )}
      </Box>
      <Box mt={4}>
        <Typography variant="h6">Version History</Typography>
        <List>
          {history.map((h, idx) => (
            <ListItem key={idx}>
              <ListItemText primary={JSON.stringify(h)} />
            </ListItem>
          ))}
        </List>
      </Box>
      <Dialog open={apiDialogOpen} onClose={() => setApiDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>API Export Response</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(apiResponse, null, 2)}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewFilePage; 